/**
 * Card "Personalizzazione per la tua azienda" — mostrata sui materiali del
 * catalogo condiviso (organization_id NULL), che non sono modificabili
 * direttamente perché appartengono a tutte le organizzazioni.
 *
 * L'org può però fissare il PROPRIO prezzo netto e il PROPRIO tempo di posa:
 * vengono salvati come delta in `org_material_customizations` e la view
 * `materials_with_pricing` li applica con priorità sul prezzo di catalogo.
 * Di conseguenza li usano configuratore, pareti certificate e preventivi.
 *
 * UNITÀ: il tempo si salva in ORE ma si mostra/edita in MINUTI (÷60 in salvataggio),
 * come nel form materiali.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pencil, RotateCcw, Save } from 'lucide-react';
import { DatabaseMaterial } from '@/hooks/useMaterials';
import { useSaveMaterialCustomization } from '@/hooks/useMaterialCustomizations';
import { useSupplierFamilies } from '@/hooks/useSupplierFamilies';
import { formatNumber } from '@/utils/format';

interface Props {
  material: DatabaseMaterial;
  /** Chiamata dopo un salvataggio/ripristino riuscito (per rinfrescare la lista). */
  onSaved?: () => void;
}

/** Campo vuoto = nessun override. Serve a distinguerlo da uno zero voluto. */
const numeroOppureNull = (v: string): number | null => {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const fmt = (n: number | null | undefined, digits = 2) =>
  n == null ? '—' : formatNumber(n, digits);

const MaterialCustomizationCard: React.FC<Props> = ({ material, onSaved }) => {
  const { mutate: save, isPending } = useSaveMaterialCustomization();

  // Valori attuali dell'override (se presenti). Tempo: ORE → MINUTI per la UI.
  const [price, setPrice] = useState('');
  const [minutes, setMinutes] = useState('');
  const [familyId, setFamilyId] = useState<string>('');
  const [waste, setWaste] = useState('');
  const [disposal, setDisposal] = useState('');
  const { data: familiesById } = useSupplierFamilies();

  useEffect(() => {
    setPrice(material.custom_net_price != null ? String(material.custom_net_price) : '');
    setMinutes(
      material.custom_installation_time_per_sqm != null
        ? String(Math.round(material.custom_installation_time_per_sqm * 60 * 1e4) / 1e4)
        : '',
    );
    setFamilyId(material.custom_family_id ?? '');
    setWaste(material.custom_waste_percentage != null ? String(material.custom_waste_percentage) : '');
    setDisposal(material.custom_disposal_percentage != null ? String(material.custom_disposal_percentage) : '');
  }, [material.id, material.custom_net_price, material.custom_installation_time_per_sqm, material.custom_family_id,
      material.custom_waste_percentage, material.custom_disposal_percentage]);

  // Famiglie selezionabili: quelle dello stesso fornitore (catalogo + proprie dell'org)
  const selectableFamilies = Array.from(familiesById?.values() ?? []).filter(
    (f) => !material.supplier_id || f.supplier_id === material.supplier_id || !!f.organization_id,
  );
  const catalogFamilyName = material.family_id
    ? familiesById?.get(material.family_id)?.name ?? material.family_code ?? '—'
    : '—';

  const catalogPrice = material.catalog_net_price ?? material.unit_price ?? null;
  const catalogMinutes =
    material.catalog_installation_time_per_sqm != null
      ? Math.round(material.catalog_installation_time_per_sqm * 60 * 1e4) / 1e4
      : null;

  const hasOverride = !!material.has_price_override || !!material.has_time_override
    || !!material.has_waste_override || !!material.has_disposal_override;

  const handleSave = () => {
    const p = price.trim() === '' ? null : Number(price.replace(',', '.'));
    const m = minutes.trim() === '' ? null : Number(minutes.replace(',', '.'));
    save(
      {
        materialId: material.id,
        customNetPrice: p != null && Number.isFinite(p) ? p : null,
        // MINUTI → ORE per il DB
        customInstallationTimeHours: m != null && Number.isFinite(m) ? m / 60 : null,
        customFamilyId: familyId || null,
        customWastePercentage: numeroOppureNull(waste),
        customDisposalPercentage: numeroOppureNull(disposal),
      },
      { onSuccess: () => onSaved?.() },
    );
  };

  const handleReset = () => {
    setPrice('');
    setMinutes('');
    setFamilyId('');
    setWaste('');
    setDisposal('');
    save(
      {
        materialId: material.id,
        customNetPrice: null,
        customInstallationTimeHours: null,
        customFamilyId: null,
        customWastePercentage: null,
        customDisposalPercentage: null,
      },
      { onSuccess: () => onSaved?.() },
    );
  };

  return (
    <Card className="border-primary/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Pencil className="h-4 w-4 text-primary" />
          Personalizzazione per la tua azienda
          {hasOverride && (
            <Badge variant="secondary" className="text-[10px]">attiva</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Questo materiale fa parte del catalogo condiviso: prezzo di listino e dati tecnici
          sono uguali per tutte le aziende e si aggiornano con il listino del fornitore.
          Qui puoi fissare <strong>il tuo prezzo</strong> e <strong>il tuo tempo di posa</strong>:
          valgono solo per la tua azienda e vengono usati in tutti i preventivi,
          comprese le pareti certificate.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="cust-price" className="text-xs">
              Il tuo prezzo ({material.unit ? `€/${material.unit}` : '€'})
            </Label>
            <Input
              id="cust-price"
              type="number"
              step="0.0001"
              min="0"
              placeholder={catalogPrice != null ? `catalogo: ${fmt(catalogPrice, 4)}` : 'es. 2,50'}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={isPending}
            />
            <p className="text-[11px] text-muted-foreground">
              Da catalogo: <strong>{fmt(catalogPrice, 4)}</strong>
              {material.list_price != null && (
                <> (listino {fmt(material.list_price, 2)}
                  {material.family_discount_pct ? ` − ${material.family_discount_pct}%` : ''})</>
              )}
              . Lascia vuoto per usare questo valore.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cust-time" className="text-xs">Il tuo tempo di posa (min/{material.unit || 'mq'})</Label>
            <Input
              id="cust-time"
              type="number"
              step="0.1"
              min="0"
              placeholder={catalogMinutes != null ? `catalogo: ${catalogMinutes}` : 'es. 6'}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              disabled={isPending}
            />
            <p className="text-[11px] text-muted-foreground">
              Da catalogo: <strong>{catalogMinutes != null ? `${catalogMinutes} min` : '—'}</strong>.
              Lascia vuoto per usare questo valore.
            </p>
          </div>

          {/* Sfrido e smaltimento: sui materiali di catalogo il modulo sotto e`
              in sola lettura (i dati sono condivisi con tutte le aziende), quindi
              questa e` l'unica strada per dire "questa lana minerale a me sfrida
              di piu' e costa di piu' smaltirla". */}
          <div className="space-y-1.5">
            <Label htmlFor="cust-waste" className="text-xs">Il tuo sfrido (%)</Label>
            <Input
              id="cust-waste"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="usa il default di categoria"
              value={waste}
              onChange={(e) => setWaste(e.target.value)}
              disabled={isPending}
            />
            <p className="text-[11px] text-muted-foreground">
              Alza le quantità da ordinare ed è un costo diretto del preventivo.
              Vuoto = default di categoria.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cust-disposal" className="text-xs">Il tuo smaltimento (%)</Label>
            <Input
              id="cust-disposal"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="usa il default dell'organizzazione"
              value={disposal}
              onChange={(e) => setDisposal(e.target.value)}
              disabled={isPending}
            />
            <p className="text-[11px] text-muted-foreground">
              Oneri di discarica sul costo di questo materiale.
              Vuoto = default dell'organizzazione.
            </p>
          </div>
        </div>

        {/* Famiglia: l'org può spostare il materiale in una propria famiglia →
            gli sconti seguono la famiglia effettiva. */}
        <div className="space-y-1.5">
          <Label htmlFor="cust-family" className="text-xs">La tua famiglia (per gli sconti)</Label>
          <select
            id="cust-family"
            value={familyId}
            onChange={(e) => setFamilyId(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-md text-sm"
            disabled={isPending}
          >
            <option value="">Usa la famiglia di catalogo — {catalogFamilyName}</option>
            {selectableFamilies.map((f) => (
              <option key={f.id} value={f.id}>
                {f.organization_id ? '★ ' : ''}{f.code} · {f.name}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-muted-foreground">
            Spostandolo in un'altra famiglia, il prezzo netto userà lo sconto di quella famiglia.
            Le famiglie con ★ sono create dalla tua azienda. Vale solo per te.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" size="sm" onClick={handleSave} disabled={isPending} className="gap-1.5">
            <Save className="h-3.5 w-3.5" />
            {isPending ? 'Salvataggio…' : 'Salva personalizzazione'}
          </Button>
          {hasOverride && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleReset}
              disabled={isPending}
              className="gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Ripristina catalogo
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MaterialCustomizationCard;
